/**
 * Vite splits the app into hash-named chunks, and each lazy route pulls its own
 * JS and CSS. After a deploy the previous hashes stop existing, so a tab that has
 * been open across the deploy asks for files the server no longer has — which the
 * browser surfaces as a failed dynamic import or, for the stylesheet half, as
 * "Unable to preload CSS for /assets/<name>.css".
 *
 * The recovery is always the same: reload, so the browser fetches the new
 * index.html and the fresh hashes. What matters is that it stays bounded — if the
 * asset is genuinely gone rather than renamed, an unconditional reload turns one
 * broken page into an endless reload loop.
 */

const RELOAD_STATE_KEY = "app:asset-reload";
const RELOAD_WINDOW_MS = 10 * 60_000;
const MAX_RELOADS_PER_WINDOW = 2;
const RELOAD_SETTLE_MS = 60_000;

/**
 * A reload has already been requested for this page load. `location.reload()`
 * does not halt execution, so anything that fails afterwards in the same tick
 * must be treated as already handled instead of queueing another reload — Vite
 * in particular reports more than one preload failure per import.
 */
let reloadPending = false;

const ASSET_ERROR_PATTERNS = [
  "unable to preload css",
  "failed to fetch dynamically imported module",
  "importing a module script failed",
  "error loading dynamically imported module",
  "failed to load module script",
  "is not a valid javascript mime type",
];

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "";
}

export function isAssetLoadError(error: unknown): boolean {
  const message = messageOf(error).toLowerCase();
  if (!message) return false;
  return ASSET_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * A stylesheet failure, as opposed to the module itself failing to load. Vite
 * reports the CSS rejections first and only reaches the module factory if none
 * of them rethrew, so cancelling this one degrades the page to unstyled instead
 * of dead — a fallback a failed JS chunk does not have.
 */
export function isCssPreloadError(error: unknown): boolean {
  return messageOf(error).toLowerCase().includes("unable to preload css");
}

function getSessionStorage(): Storage | null {
  // Accessing sessionStorage throws outright in some privacy configurations.
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function readReloadState(storage: Storage): { first: number; count: number } | null {
  try {
    const raw = storage.getItem(RELOAD_STATE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { first?: unknown; count?: unknown };
    const first = Number(parsed.first);
    const count = Number(parsed.count);
    if (!Number.isFinite(first) || !Number.isFinite(count)) return null;

    return { first, count };
  } catch {
    return null;
  }
}

/**
 * Reloads unless this tab has already spent its reload budget. Returns whether a
 * reload was triggered (or is already in flight), so callers can decide what to
 * do when recovery is no longer an option.
 */
export function reloadForAssetError(): boolean {
  if (reloadPending) return true;

  const storage = getSessionStorage();
  // Without storage we cannot bound the retries across reloads, and an unbounded
  // reload loop is worse than the error screen, which offers a manual retry.
  if (!storage) return false;

  const now = Date.now();
  const previous = readReloadState(storage);
  const withinWindow = previous !== null && now - previous.first < RELOAD_WINDOW_MS;
  const attempts = withinWindow && previous !== null ? previous.count : 0;

  // A hard cap, not a cooldown: rate-limiting alone still loops forever whenever
  // the next failure takes longer to surface than the cooldown lasts.
  if (attempts >= MAX_RELOADS_PER_WINDOW) return false;

  try {
    storage.setItem(RELOAD_STATE_KEY, JSON.stringify({
      first: withinWindow && previous !== null ? previous.first : now,
      count: attempts + 1,
    }));
  } catch {
    return false;
  }

  reloadPending = true;
  window.location.reload();
  return true;
}

/**
 * Forgets past failures once the app has clearly recovered, so that a later,
 * unrelated deploy still gets a full reload budget.
 *
 * Deliberately time-based rather than tied to the `load` event: in a stale-deploy
 * loop the document itself always loads fine — it is the lazy chunks that fail —
 * so resetting on load would cancel the cap it is meant to preserve.
 */
function scheduleBudgetReset(): void {
  window.setTimeout(() => {
    if (reloadPending) return;

    const storage = getSessionStorage();
    if (!storage) return;
    try {
      storage.removeItem(RELOAD_STATE_KEY);
    } catch {
      // Nothing to do: the budget simply expires with RELOAD_WINDOW_MS instead.
    }
  }, RELOAD_SETTLE_MS);
}

/**
 * Vite emits `vite:preloadError` when a lazily imported chunk or its stylesheet
 * fails to load, and rethrows unless the event is cancelled. Handling it here is
 * the only way to catch the CSS half: that failure happens inside Vite's preload
 * helper and never reaches render.
 */
export function installAssetReloadHandler(): void {
  scheduleBudgetReset();

  window.addEventListener("vite:preloadError", (event: Event) => {
    const { payload } = event as Event & { payload?: unknown };

    // Schedule the recovery regardless of which half failed. This is capped, and
    // a reload already in flight is not queued twice.
    reloadForAssetError();

    // Whether to cancel depends only on WHAT failed, never on whether a reload
    // was scheduled. Vite calls the module factory after this handler and reports
    // its failure through the same event, so cancelling a *module* failure makes
    // __vitePreload resolve with undefined — React.lazy then dies reading
    // `.default`, with a TypeError that no longer looks like an asset problem.
    // Only the stylesheet half is safe to swallow; the module half has to keep
    // throwing so the error boundary can recognise and handle it.
    if (isCssPreloadError(payload)) event.preventDefault();
  });
}
